import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Compact "this gameplay landed on Solana" receipt.
 *
 * Renders nothing when `receipt` is falsy or `receipt.attempted` is false
 * (i.e. Demo / Normal mode — no on-chain attempt was made).
 *
 * States:
 *   - success: green-tinted card with Solscan deep-link.
 *   - failure (RPC retry / timeout): amber-tinted card with the technical reason,
 *     no link (nothing landed).
 *
 * Props:
 *   receipt = {
 *     attempted: boolean,
 *     tx_signature: string | null,
 *     solscan_url: string | null,
 *     explorer_url: string | null,
 *     sp_awarded: number,
 *     season_number: number | null,
 *     onchain_error: string | null,
 *     cluster: 'devnet' | 'mainnet-beta' | string,
 *     game_type: string,
 *   }
 *   compact = boolean (optional — drops paddings for inline use)
 */
export default function OnChainReceipt({ receipt, compact = false }) {
  if (!receipt || receipt.attempted === false) return null;

  const success = Boolean(receipt.tx_signature);
  const clusterLabel = (receipt.cluster || 'devnet') === 'mainnet-beta' ? 'Mainnet' : 'Devnet';
  const sigShort = receipt.tx_signature
    ? `${receipt.tx_signature.slice(0, 6)}…${receipt.tx_signature.slice(-6)}`
    : '';

  const palette = success
    ? {
        border: 'rgba(34, 197, 94, 0.35)',
        glow: '0 0 16px rgba(34, 197, 94, 0.18), inset 0 0 12px rgba(34, 197, 94, 0.08)',
        bg: 'linear-gradient(135deg, rgba(34,197,94,0.08) 0%, rgba(34,197,94,0.02) 100%)',
        accent: '#22c55e',
        accentSoft: 'rgba(34, 197, 94, 0.7)',
      }
    : {
        border: 'rgba(245, 158, 11, 0.35)',
        glow: '0 0 16px rgba(245, 158, 11, 0.18), inset 0 0 12px rgba(245, 158, 11, 0.06)',
        bg: 'linear-gradient(135deg, rgba(245,158,11,0.08) 0%, rgba(245,158,11,0.02) 100%)',
        accent: '#f59e0b',
        accentSoft: 'rgba(245, 158, 11, 0.7)',
      };

  return (
    <AnimatePresence>
      <motion.div
        key={receipt.tx_signature || `pending-${receipt.game_type}`}
        initial={{ opacity: 0, y: 8, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        data-testid={success ? 'onchain-receipt-success' : 'onchain-receipt-failure'}
        style={{
          padding: compact ? '8px 12px' : '12px 14px',
          borderRadius: 10,
          border: `1px solid ${palette.border}`,
          background: palette.bg,
          boxShadow: palette.glow,
          backdropFilter: 'blur(8px)',
          fontFamily: "'Courier New', monospace",
          maxWidth: 360,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {success ? (
            <CheckCircle2 size={18} style={{ color: palette.accent, flexShrink: 0, marginTop: 1 }} />
          ) : (
            <AlertTriangle size={18} style={{ color: palette.accent, flexShrink: 0, marginTop: 1 }} />
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.12em',
                color: palette.accent,
                textTransform: 'uppercase',
                fontWeight: 700,
              }}
            >
              {success
                ? `Confirmed on Solana ${clusterLabel}`
                : `Off-chain only · RPC retry`}
            </div>

            <div
              style={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.9)',
                marginTop: 2,
                fontWeight: 600,
              }}
            >
              +{Number(receipt.sp_awarded || 0).toLocaleString()} SP submitted
              {receipt.season_number ? ` · Season ${receipt.season_number}` : ''}
            </div>

            {success ? (
              <a
                href={receipt.solscan_url || receipt.explorer_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="onchain-receipt-solscan-link"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 8,
                  fontSize: 12,
                  color: palette.accentSoft,
                  textDecoration: 'none',
                  borderBottom: `1px dashed ${palette.accentSoft}`,
                  paddingBottom: 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = palette.accent)}
                onMouseLeave={(e) => (e.currentTarget.style.color = palette.accentSoft)}
              >
                View on Solscan {sigShort}
                <ExternalLink size={11} />
              </a>
            ) : (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.55)',
                  fontStyle: 'italic',
                }}
                title={receipt.onchain_error || ''}
              >
                SP recorded off-chain — will retry next round
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Inline "pending" placeholder for the brief window between submit and response.
 */
export function OnChainReceiptPending() {
  return (
    <div
      data-testid="onchain-receipt-pending"
      style={{
        padding: '10px 14px',
        borderRadius: 10,
        border: '1px solid rgba(148,163,184,0.25)',
        background: 'rgba(148,163,184,0.05)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: "'Courier New', monospace",
        maxWidth: 360,
      }}
    >
      <Loader2 size={16} className="animate-spin" style={{ color: 'rgba(148,163,184,0.8)' }} />
      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.05em' }}>
        Submitting to Solana…
      </span>
    </div>
  );
}

/**
 * Convenience helper: dispatch a sonner toast matching the receipt state.
 * Pass the `toast` import from 'sonner' to avoid forcing the dependency on
 * components that don't use toasts.
 */
export function fireOnChainToast(toast, receipt) {
  if (!receipt || receipt.attempted === false) return;
  const cluster = (receipt.cluster || 'devnet') === 'mainnet-beta' ? 'Mainnet' : 'Devnet';
  if (receipt.tx_signature) {
    const url = receipt.solscan_url || receipt.explorer_url;
    toast.success(`+${Number(receipt.sp_awarded || 0).toLocaleString()} SP submitted on ${cluster}`, {
      description: `Tx: ${receipt.tx_signature.slice(0, 8)}…${receipt.tx_signature.slice(-8)}`,
      action: url
        ? {
            label: 'View on Solscan',
            onClick: () => window.open(url, '_blank', 'noopener,noreferrer'),
          }
        : undefined,
      duration: 8000,
    });
    // eslint-disable-next-line no-console
    console.log(
      `[on-chain ✓] ${receipt.game_type} · +${receipt.sp_awarded} SP · season ${receipt.season_number} · sig=${receipt.tx_signature}\n${url}`,
    );
  } else {
    toast.warning('Recorded off-chain · RPC retry', {
      description: receipt.onchain_error || 'Will retry next round',
      duration: 5000,
    });
    // eslint-disable-next-line no-console
    console.warn(`[on-chain ✗] ${receipt.game_type} · err=${receipt.onchain_error}`);
  }
}
